import { Box, Skeleton, Stack } from '@mui/material'
import {
	deviationLabels,
	IMilkingProcessDeviationItem,
} from 'models/MilkingProcess'
import { useMemo } from 'react'
import { COLORS } from 'styles/colors'

import { ReactComponent as CircleHelpLogo } from 'assets/icons/circle-help.svg'
import { CardStandart } from 'ui/CardStandart'
import { Scrollbar } from 'ui/scrollbar/Scrollbar'
import TooltipHover from 'ui/TooltipHover'
import { NoDataPlaceholder } from 'components/milking-process/shared/NoDataPlaceholder'
import { TypographyHeader } from 'ui/typography/TypographyHeader'
import { TypographyHeader14 } from 'ui/typography/TypographyHeader14'
import { TypographyPrimary } from 'ui/typography/TypographyPrimary'
import { TypographySecondary } from 'ui/typography/TypographySecondary'
import { TypographySecondary14 } from 'ui/typography/TypographySecondary14'
type MilkingProcesDeviationsCardProps = {
	deviations: IMilkingProcessDeviationItem[] | undefined
	isLoading: boolean
	hasNoSourceData?: boolean
}

export const MilkingProcesDeviationsCard = (
	props: MilkingProcesDeviationsCardProps,
) => {
	const totalDeviations = useMemo(() => {
		if (!props.deviations || props.deviations.length === 0) return 0
		return props.deviations.reduce((sum, el) => sum + el.count, 0)
	}, [props.deviations])

	const getPercentage = (count: number) => {
		if (totalDeviations === 0) return 0
		return Math.round((count / totalDeviations) * 100)
	}
	const showNoData =
		!props.isLoading && (props.hasNoSourceData || !props.deviations)
	const showNoDeviations =
		!props.isLoading && !showNoData && (props.deviations?.length ?? 0) === 0

	return (
		<CardStandart
			sx={{
				p: '24px 0px',
				width: '100%',
				height: '100%',
				display: 'flex',
				flexDirection: 'column',
			}}
		>
			<Stack
				direction='row'
				justifyContent='space-between'
				alignItems='center'
				pr='12px'
			>
				<TypographyHeader sx={{ p: '0 24px' }}>
					Частые отклонения
				</TypographyHeader>
				<TooltipHover
					title={
						'Процент отклонений рассчитывается от общего количества отклонений по всем доильным залам и по всем сменам за выбранный период.'
					}
					placement={'top'}
				>
					<Box
						sx={{
							'&:hover svg': {
								fill: COLORS.main,
							},
						}}
					>
						<CircleHelpLogo
							fill={COLORS.icon}
							style={{
								width: '16px',
								height: '16px',
							}}
						/>
					</Box>
				</TooltipHover>
			</Stack>
			{props.isLoading ? (
				<Stack sx={{ p: '0 24px', mt: '12px', gap: '8px' }}>
					{[...Array(props.deviations?.length || 8)].map((_, index) => (
						<Skeleton key={index} width='100%' height={20} />
					))}
				</Stack>
			) : showNoData ? (
				<NoDataPlaceholder />
			) : showNoDeviations ? (
				<Stack flex={1} justifyContent={'center'} alignItems={'center'}>
					<TypographySecondary14>
						Отклонений не обнаружено
					</TypographySecondary14>
				</Stack>
			) : (
				<>
					<Stack
						direction={'row'}
						justifyContent={'space-between'}
						alignItems={'center'}
						sx={{ p: '0 24px', mt: '24px' }}
					>
						<TypographySecondary>Тип отклонения</TypographySecondary>
						<TypographySecondary>Количество</TypographySecondary>
					</Stack>
					<Scrollbar sx={{ maxHeight: '280px' }}>
						{props.deviations?.map(el => (
							<Stack
								key={el.type}
								direction={'row'}
								justifyContent={'space-between'}
								alignItems={'center'}
								sx={{
									ml: '16px',
									mr: '16px',
									px: '8px',
									py: '8px',
									borderRadius: '12px',
									transition: 'background-color 0.2s',
									'&:hover': {
										backgroundColor: COLORS.background,
									},
								}}
							>
								<TypographyPrimary>
									{deviationLabels[el.type]}
								</TypographyPrimary>
								<Stack direction={'row'} gap={'12px'} alignItems={'center'}>
									<TypographyHeader14>{el.count}</TypographyHeader14>
									<TypographySecondary>
										{getPercentage(el.count)}%
									</TypographySecondary>
								</Stack>
							</Stack>
						))}
					</Scrollbar>
				</>
			)}
		</CardStandart>
	)
}
